import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/PackagesSlider.css';
import GradientIcon from './GradientIcon';

const PackagesSlider = () => {
  const [expandedPackages, setExpandedPackages] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const sliderRef = useRef(null);

  const ALL_PACKAGES = [
    {
      id: 'standard-economy',
      title: 'Early Bird',
      description: 'Pachet Early Bird pentru participare',
      featured: false,
      benefits: [
        'Brain Mapping (în valoare de 500€)',
        'Locuri în zonele: J, K, L și M',
        'Acces complet la eveniment (11:00 - 19:00)'
      ],
      brainMappingPeriod: 'programare după eveniment'
    },
    {
      id: 'standard-discount',
      title: 'Standard',
      description: 'Pachet cu discount special',
      featured: false,
      benefits: [
        'Brain Mapping (în valoare de 500€)',
        'Locuri în zonele: J, K, L, M și loje',
        'Acces complet la eveniment (11:00 - 19:00)',
        'Minge Antistres'
      ],
      brainMappingPeriod: null
    },
    {
      id: 'standard',
      title: 'Standard',
      description: 'Pachet de bază pentru participare',
      featured: false,
      benefits: [
        'Brain Mapping (în valoare de 500€)',
        'Locuri în zonele: F, G și loje',
        'Acces complet la eveniment (11:00 - 19:00)',
        'Minge Antistres'
      ],
      brainMappingPeriod: 'programare după eveniment'
    },
    {
      id: 'standard-premium',
      title: 'Standard Premium',
      description: 'Pachet standard îmbunătățit',
      featured: false,
      benefits: [
        'Brain Mapping (în valoare de 500€)',
        'Locuri premium în zonele: H și I',
        'Acces complet la eveniment (11:00 - 19:00)',
        'Minge Antistres'
      ],
      brainMappingPeriod: 'programare după eveniment'
    },
    {
      id: 'vip',
      title: 'VIP',
      description: 'Experiență premium și beneficii speciale',
      featured: true,
      benefits: [
        'Brain Mapping cu Diana (în valoare de 1000€)',
        'Locuri în fața scenei, în zonele: A, B, C, D și E',
        'Acces complet la eveniment (11:00 - 19:00)',
        'Acces în comunitatea VIP',
        'Cupon pentru prânz',
        'Brain Notes – workbook fizic',
        'Minge Antistres',
        'Goodie Bag (în valoare de 100€)'
      ],
      brainMappingPeriod: 'programare după eveniment'
    },
    {
      id: 'ultra-vip',
      title: 'Ultra VIP',
      description: 'Experiență exclusivă',
      featured: false,
      benefits: [
        'Brain Mapping cu Diana (în valoare de 1000€)',
        'Locuri în primele 3 rânduri în zonele: A, D și E',
        'Acces prioritar în sală',
        'Acces complet la eveniment (11:00 - 19:00)',
        'Acces în comunitatea VIP',
        'Masă / bufet separat pentru Ultra VIP',
        'Cină privată cu speakerii',
        'Networking cu speakerii',
        'Prioritate la întrebările adresate în Q&A',
        'Brain Notes – workbook fizic',
        'Minge Antistres',
        'Goodie Bag (în valoare de 200€)',
        'Aparatul de stimulare a nervului vag (în valoare de 1000€)'
      ],
      brainMappingPeriod: 'programare prioritară din momentul achiziției biletului'
    }
  ];

  const visiblePackages = ALL_PACKAGES.filter((pkg) => pkg.id !== 'standard-discount');

  const handlePrevPackage = () => {
    if (!sliderRef.current) return;
    const cardWidth = 380 + 24; // card width + gap
    sliderRef.current.scrollLeft -= cardWidth;
  };

  const handleNextPackage = () => {
    if (!sliderRef.current) return;
    const cardWidth = 380 + 24; // card width + gap
    sliderRef.current.scrollLeft += cardWidth;
  };

  const toggleExpandPackage = (packageId) => {
    setExpandedPackages(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  const handleMouseDown = useCallback((e) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  // Add event listeners for drag functionality
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener('mousedown', handleMouseDown);
    slider.addEventListener('mouseleave', handleMouseLeave);
    slider.addEventListener('mouseup', handleMouseUp);
    slider.addEventListener('mousemove', handleMouseMove);

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      slider.removeEventListener('mouseleave', handleMouseLeave);
      slider.removeEventListener('mouseup', handleMouseUp);
      slider.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseDown, handleMouseLeave, handleMouseUp, handleMouseMove]);

  return (
    <section className="packages-section" id="pachete">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Pachete și locuri</h2>
          <p className="section-subtitle">Alege varianta potrivită pentru tine</p>
        </div>

        {/* Package Slider Wrapper */}
        <div className="packages-slider-wrapper">
          {/* Navigation Buttons */}
          <button 
            className="slider-nav slider-nav-prev" 
            onClick={handlePrevPackage}
            aria-label="Pachetul anterior"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <button 
            className="slider-nav slider-nav-next" 
            onClick={handleNextPackage}
            aria-label="Următorul pachet"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Package Slider Container */}
          <div 
            ref={sliderRef}
            className={`packages-slider-container ${isDragging ? 'dragging' : ''}`}
          >
            {/* Slider Track */}
            <div className="packages-slider-track">
              {visiblePackages.map((pkg) => {
                const isExpanded = expandedPackages[pkg.id];
                const visibleBenefits = isExpanded ? pkg.benefits : pkg.benefits.slice(0, 4);
                const hasMoreBenefits = pkg.benefits.length > 4;

                return (
                  <div 
                    key={pkg.id} 
                    className={`package-pricing-card ${pkg.featured ? 'package-pricing-featured' : ''}`}
                    style={pkg.featured ? {backgroundImage: 'url(/assets/background-cards.webp)'} : {}}
                  >
                    {pkg.featured && <div className="package-pricing-badge">Cel mai popular</div>}
                    
                    <div className="package-pricing-header">
                      <h3 className="package-pricing-title">{pkg.title}</h3>
                      <p className="package-pricing-description">{pkg.description}</p>
                    </div>

                    <div className="package-pricing-content">
                      <p className="package-pricing-label">Ce este inclus:</p>
                      <ul className="package-pricing-benefits">
                        {visibleBenefits.map((benefit, idx) => (
                          <li key={idx} className={benefit.includes('Brain Mapping') ? 'package-benefit-with-period' : ''}>
                            <div className="package-checkmark">
                              <GradientIcon type="checkmark" />
                            </div>
                            <span>
                              {benefit}
                              {pkg.brainMappingPeriod && benefit.includes('Brain Mapping') && (
                                <span className="benefit-period">{pkg.brainMappingPeriod}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {hasMoreBenefits && (
                        <button 
                          className="package-expand-btn"
                          onClick={() => toggleExpandPackage(pkg.id)}
                        >
                          {isExpanded ? 'Afișează mai puțin' : 'Afișează mai multe'}
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 16 16" 
                            fill="none" 
                            style={{
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.3s ease'
                            }}
                          >
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="package-pricing-footer">
                      <a 
                        href="/formular-inscriere" 
                        className={`btn ${pkg.featured ? 'btn-primary' : 'btn-outline'} btn-large`}
                      >
                        {pkg.featured ? 'Vreau pachetul VIP' : 'Solicită ofertă'}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PackagesSlider;
